import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sparkles, Settings, History, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { adaptiveMacrosApi } from '../api/adaptiveMacros';
import AdaptiveMacroHistoryDialog from './AdaptiveMacroHistoryDialog';

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const AdaptiveMacroCard = ({ clientId = null }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastAdjustment, setLastAdjustment] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const humanGoal = (goal, cfg) => {
    if (goal === 'cut') return t('adaptiveMacroCard.goalCutting');
    if (goal === 'bulk') return t('adaptiveMacroCard.goalBulking');
    if (goal === 'recomp') return t('adaptiveMacroCard.goalRecomp');
    if (goal === 'reverse') {
      const wk = cfg?.reverse_config?.week_number;
      return wk != null ? t('adaptiveMacroCard.goalReverseWeek', { week: wk }) : t('adaptiveMacroCard.goalReverse');
    }
    return t('adaptiveMacroCard.goalMaintaining');
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await adaptiveMacrosApi.getConfig(clientId);
        if (!alive) return;
        setConfig(data?.config || null);
        if (data?.config?.enabled) {
          const adjRes = await adaptiveMacrosApi.listAdjustments(clientId, 1);
          if (alive) setLastAdjustment(adjRes?.data?.adjustments?.[0] || null);
        }
      } catch {
        // silent — card just doesn't render
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [clientId]);

  if (loading) return null;
  if (!config || !config.enabled) {
    // Self-view: no card when adaptive is off — clients enable via the
    // Recalculate Macros wizard (which is itself locked when a coach manages
    // the diet, so both modes can't collide).
    if (!clientId) return null;
    // Coach-on-behalf view: still expose the enable CTA so a coach can turn
    // adaptive on for a client.
    return (
      <Card className="bg-gradient-to-br from-zinc-900 via-zinc-950 to-black border-white/10 text-white overflow-hidden relative">
        <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#FF5C00]/20 blur-3xl" />
        <div className="relative p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="h-10 w-10 rounded-full bg-[#FF5C00]/20 flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-[#FF5C00]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{t('adaptiveMacroCard.title')}</div>
              <div className="text-xs text-white/60 truncate">{t('adaptiveMacroCard.subtitle')}</div>
            </div>
          </div>
          <Button
            size="sm"
            className="bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white shadow-[0_0_24px_rgba(255,107,47,0.35)] shrink-0"
            onClick={() => navigate('/adaptive-macros/settings' + (clientId ? `?client_id=${clientId}` : ''))}
          >
            {t('adaptiveMacroCard.enable')}
          </Button>
        </div>
      </Card>
    );
  }

  const next = daysUntil(config.next_adjustment_at);
  const decision = lastAdjustment?.decision;
  const kcalDelta = lastAdjustment
    ? (lastAdjustment.new_targets?.kcal || 0) - (lastAdjustment.previous_targets?.kcal || 0)
    : 0;
  const TrendIcon = kcalDelta > 0 ? TrendingUp : kcalDelta < 0 ? TrendingDown : Minus;

  return (
    <>
      <Card className="bg-gradient-to-br from-zinc-900 via-zinc-950 to-black border-white/10 text-white overflow-hidden relative">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#FF5C00]/20 blur-3xl" />
        <div className="relative p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="h-4 w-4 text-[#FF5C00] shrink-0" />
              <span className="text-xs font-semibold uppercase tracking-wider text-white/80">{t('adaptiveMacroCard.title')}</span>
              <Badge
                variant="outline"
                className={`border-white/20 text-[10px] px-1.5 py-0 h-4 ${config.goal === 'reverse' ? 'text-[#FF5C00] cursor-pointer' : 'text-white/70'}`}
                onClick={config.goal === 'reverse' ? () => navigate('/reverse-diet' + (clientId ? `?client_id=${clientId}` : '')) : undefined}
              >
                {humanGoal(config.goal, config)}
              </Badge>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 md:h-7 md:w-7 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => setHistoryOpen(true)}
                aria-label={t('adaptiveMacroCard.history')}
              >
                <History className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 md:h-7 md:w-7 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/adaptive-macros/settings' + (clientId ? `?client_id=${clientId}` : ''))}
                aria-label={t('adaptiveMacroCard.settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-end gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/50">{t('adaptiveMacroCard.today')}</div>
              <div className="text-2xl font-bold tabular-nums">{config.current_targets?.kcal ?? '—'}</div>
              <div className="text-[10px] text-white/50">kcal</div>
            </div>
            <div className="flex gap-3 text-[11px] text-white/70 pb-1">
              <span>P <span className="text-white font-semibold tabular-nums">{config.current_targets?.protein_g ?? 0}</span>g</span>
              <span>C <span className="text-white font-semibold tabular-nums">{config.current_targets?.carbs_g ?? 0}</span>g</span>
              <span>F <span className="text-white font-semibold tabular-nums">{config.current_targets?.fat_g ?? 0}</span>g</span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1 border-t border-white/10 text-[11px] text-white/60">
            {lastAdjustment ? (
              <span className="flex items-center gap-1 min-w-0 truncate">
                <TrendIcon className={`h-3 w-3 shrink-0 ${kcalDelta > 0 ? 'text-green-400' : kcalDelta < 0 ? 'text-red-400' : 'text-white/40'}`} />
                <span className="truncate">
                  {decision === 'hold' ? t('adaptiveMacroCard.heldOnTrack') : `${kcalDelta > 0 ? '+' : ''}${kcalDelta} kcal`} · {new Date(lastAdjustment.adjusted_at).toLocaleDateString()}
                </span>
              </span>
            ) : (
              <span>{t('adaptiveMacroCard.noAdjustments')}</span>
            )}
            <span className="shrink-0">
              {next != null ? (next === 0 ? t('adaptiveMacroCard.dueNow') : t('adaptiveMacroCard.nextIn', { count: next })) : ''}
            </span>
          </div>
        </div>
      </Card>
      {historyOpen && (
        <AdaptiveMacroHistoryDialog
          clientId={clientId}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </>
  );
};

export default AdaptiveMacroCard;
